// src/scripts/sqlApiHelper/pettycashApi.js
// ✅ Firestore implementation (venue-scoped; no collectionGroup queries)
//
// Adds/supports: is_claimed_back boolean on entries
// Defensive legacy support: is_calimed_back typo is also read/written.
// Totals exclude claimed-back entries.
// 🔒 once claimed-back is true, it cannot be reverted to false.
//
// FIXED VERSION:
// - preserves original API contract
// - preserves expense_date as canonical entry date
// - preserves late_entries behaviour
// - updates week notes on existing week doc when provided
// - ADDS unresolved/unclaimed backlog loading for PettyCashView
// - PettyCashView can now list old unclaimed entries from previous weeks
// - ADDS revenue helpers for Petty Cash vs Revenue KPI
//   using tills_days/{date}/tills/* doc totals
//   declared_total first, counted_total fallback

import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { db, auth } from "@/firebase";

/* ===================== Small helpers ===================== */

function isISODate(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));
}

function parseISOToUTC(iso) {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

function toISODateOnlyUTC(d) {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysUTC(isoDate, days) {
  const dt = parseISOToUTC(isoDate);
  if (!dt) return isoDate;
  dt.setUTCDate(dt.getUTCDate() + days);
  return toISODateOnlyUTC(dt);
}

function num2(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
}

function currentUserAudit() {
  const u = auth?.currentUser;
  return {
    uid: u?.uid || null,
    email: u?.email || null,
  };
}

function toMillis(ts) {
  if (!ts) return 0;
  if (ts?.toMillis) return ts.toMillis();
  const t = Date.parse(String(ts));
  return Number.isFinite(t) ? t : 0;
}

function toBool(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (typeof v === "string") return v.toLowerCase() === "true";
  return Boolean(v);
}

function isClaimedBack(row) {
  return toBool(row?.is_claimed_back) || toBool(row?.is_calimed_back);
}

function sumUnclaimed(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((s, r) => {
    if (isClaimedBack(r)) return s;
    return s + Number(r?.amount || 0);
  }, 0);
}

function getWeekRangeDates(week_ending_date) {
  if (!isISODate(week_ending_date)) return [];

  const from = addDaysUTC(week_ending_date, -6);
  const dates = [];

  let cur = from;
  while (cur <= week_ending_date) {
    dates.push(cur);
    cur = addDaysUTC(cur, 1);
  }

  return dates;
}

function normaliseEntryRow(row, fallbackWeekEnding = "") {
  const sourceWeekEnding =
    String(row?.week_ending_date || fallbackWeekEnding || "").trim();

  return {
    ...row,

    // canonical field
    is_claimed_back: isClaimedBack(row),

    // legacy typo mirror, in case older docs/UI referenced this spelling
    is_calimed_back: isClaimedBack(row),

    source_week_ending_date: sourceWeekEnding,
    _source_week_ending_date: sourceWeekEnding,
  };
}

/* =========================================================
   LOAD OUTSTANDING UNCLAIMED ENTRIES ACROSS PREVIOUS WEEKS

   This is the important fix for PettyCashView.

   It scans venue-scoped petty_cash_weeks up to the selected week
   and returns entries where is_claimed_back / is_calimed_back is false.
========================================================= */
async function loadOutstandingUnclaimedEntries({
  venue_id,
  week_ending_date,
  exclude_week_ending_date = "",
  maxWeeks = 100,
} = {}) {
  if (!venue_id) throw new Error("Missing venue_id");
  if (!week_ending_date) throw new Error("Missing week_ending_date");
  if (!isISODate(week_ending_date)) {
    throw new Error("week_ending_date must be YYYY-MM-DD");
  }

  const weeksCol = collection(db, "venues", venue_id, "petty_cash_weeks");

  const weeksQ = query(
    weeksCol,
    where("week_ending_date", "<=", week_ending_date),
    orderBy("week_ending_date", "desc"),
    limit(maxWeeks)
  );

  const weeksSnap = await getDocs(weeksQ);

  const rows = [];

  for (const w of weeksSnap.docs) {
    const sourceWeekEnding = String(w.id || "");

    if (
      exclude_week_ending_date &&
      sourceWeekEnding === String(exclude_week_ending_date)
    ) {
      continue;
    }

    const wRef = doc(db, "venues", venue_id, "petty_cash_weeks", sourceWeekEnding);
    const entriesRef = collection(wRef, "petty_cash_entries");

    const entriesQ = query(
      entriesRef,
      orderBy("expense_date", "asc"),
      orderBy("created_at", "asc")
    );

    const snap = await getDocs(entriesQ);

    for (const d of snap.docs) {
      const row = normaliseEntryRow(
        {
          id: d.id,
          ...d.data(),
        },
        sourceWeekEnding
      );

      if (!isClaimedBack(row)) {
        rows.push(row);
      }
    }
  }

  rows.sort((a, b) => {
    const ea = String(a?.expense_date || "");
    const eb = String(b?.expense_date || "");

    if (ea < eb) return -1;
    if (ea > eb) return 1;

    const wa = String(a?._source_week_ending_date || "");
    const wb = String(b?._source_week_ending_date || "");

    if (wa < wb) return -1;
    if (wa > wb) return 1;

    return toMillis(a?.created_at) - toMillis(b?.created_at);
  });

  return rows;
}

/* =========================================================
   LIST PETTY CASH WEEK + ENTRIES (+ late_entries)
========================================================= */
export async function pettyCashWeekList({ venue_id, week_ending_date } = {}) {
  if (!venue_id) throw new Error("Missing venue_id");
  if (!week_ending_date) throw new Error("Missing week_ending_date");
  if (!isISODate(week_ending_date)) {
    throw new Error("week_ending_date must be YYYY-MM-DD");
  }

  const week_start_date = addDaysUTC(week_ending_date, -6);

  const weekStartUTC = parseISOToUTC(week_start_date);
  const weekEndExclusiveUTC = parseISOToUTC(addDaysUTC(week_ending_date, 1));
  const weekStartTS = Timestamp.fromDate(weekStartUTC);
  const weekEndExclusiveTS = Timestamp.fromDate(weekEndExclusiveUTC);

  const weekRef = doc(db, "venues", venue_id, "petty_cash_weeks", week_ending_date);
  const weekSnap = await getDoc(weekRef);

  if (!weekSnap.exists()) {
    const outstanding_entries = await loadOutstandingUnclaimedEntries({
      venue_id,
      week_ending_date,
      exclude_week_ending_date: week_ending_date,
    });

    return {
      ok: true,
      venue_id,
      week_ending_date,
      week_start_date,
      week: null,
      entries: [],
      late_entries: [],
      outstanding_entries,
    };
  }

  const week = { id: weekSnap.id, ...weekSnap.data() };

  const entriesRef = collection(weekRef, "petty_cash_entries");
  const entriesQ = query(
    entriesRef,
    orderBy("expense_date", "asc"),
    orderBy("created_at", "asc")
  );

  const entriesSnap = await getDocs(entriesQ);
  const entries = entriesSnap.docs.map((d) =>
    normaliseEntryRow(
      {
        id: d.id,
        ...d.data(),
      },
      week_ending_date
    )
  );

  const weeksCol = collection(db, "venues", venue_id, "petty_cash_weeks");

  const weeksQ = query(
    weeksCol,
    where("week_ending_date", "<=", week_ending_date),
    orderBy("week_ending_date", "desc"),
    limit(20)
  );

  const weeksSnap = await getDocs(weeksQ);

  const late_entries = [];

  for (const w of weeksSnap.docs) {
    const we = String(w.id || "");
    if (we === week_ending_date) continue;

    const wRef = doc(db, "venues", venue_id, "petty_cash_weeks", we);
    const wEntriesRef = collection(wRef, "petty_cash_entries");

    const createdThisWeekQ = query(
      wEntriesRef,
      where("created_at", ">=", weekStartTS),
      where("created_at", "<", weekEndExclusiveTS),
      orderBy("created_at", "desc")
    );

    const snap = await getDocs(createdThisWeekQ);

    for (const d of snap.docs) {
      const row = normaliseEntryRow(
        {
          id: d.id,
          ...d.data(),
        },
        we
      );

      const exp = String(row?.expense_date || "");
      if (exp && (exp < week_start_date || exp > week_ending_date)) {
        late_entries.push(row);
      }
    }
  }

  late_entries.sort((a, b) => {
    const ea = String(a?.expense_date || "");
    const eb = String(b?.expense_date || "");

    if (ea < eb) return -1;
    if (ea > eb) return 1;

    return toMillis(b?.created_at) - toMillis(a?.created_at);
  });

  const outstanding_entries = await loadOutstandingUnclaimedEntries({
    venue_id,
    week_ending_date,
    exclude_week_ending_date: week_ending_date,
  });

  return {
    ok: true,
    venue_id,
    week_ending_date,
    week_start_date,
    week,
    entries,
    late_entries,
    outstanding_entries,
  };
}

/* =========================================================
   UPSERT PETTY CASH WEEK + ENTRY
   🔒 once claimed-back is true, it cannot be reverted
========================================================= */
export async function pettyCashWeekUpsert(payload = {}) {
  const {
    venue_id,
    week_ending_date,
    expense_date,
    date_of_expense,
    category,
    description,
    amount,
    id,
    week_notes,
    is_claimed_back,
    is_calimed_back,
  } = payload;

  if (!venue_id) throw new Error("Missing venue_id");
  if (!week_ending_date) throw new Error("Missing week_ending_date");
  if (!isISODate(week_ending_date)) {
    throw new Error("week_ending_date must be YYYY-MM-DD");
  }

  const finalExpenseDate = expense_date ?? date_of_expense;

  if (!finalExpenseDate) throw new Error("Missing expense_date");
  if (!isISODate(finalExpenseDate)) {
    throw new Error("expense_date must be YYYY-MM-DD");
  }

  if (!category) throw new Error("Missing category");
  if (!description) throw new Error("Missing description");

  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    throw new Error("Invalid amount");
  }

  let claimed = toBool(is_claimed_back) || toBool(is_calimed_back);

  const week_start_date = addDaysUTC(week_ending_date, -6);
  const audit = currentUserAudit();

  const weekRef = doc(db, "venues", venue_id, "petty_cash_weeks", week_ending_date);
  const weekSnap = await getDoc(weekRef);

  if (!weekSnap.exists()) {
    await setDoc(weekRef, {
      venue_id,
      week_ending_date,
      week_start_date,
      notes: week_notes ? String(week_notes) : null,

      created_by_uid: audit.uid,
      created_by_email: audit.email,
      created_at: Timestamp.now(),

      updated_by_uid: audit.uid,
      updated_by_email: audit.email,
      updated_at: Timestamp.now(),
    });
  } else {
    const weekUpdate = {
      updated_by_uid: audit.uid,
      updated_by_email: audit.email,
      updated_at: Timestamp.now(),
    };

    if (week_notes !== undefined) {
      weekUpdate.notes = week_notes ? String(week_notes) : null;
    }

    await updateDoc(weekRef, weekUpdate);
  }

  const entriesRef = collection(weekRef, "petty_cash_entries");

  let entryId = id ? String(id) : null;
  let entryRef = null;

  const baseFields = {
    venue_id,
    week_ending_date,
    expense_date: finalExpenseDate,
    category: String(category),
    description: String(description).trim(),
    amount: num2(numericAmount),
    is_claimed_back: claimed,

    // defensive legacy mirror for typo compatibility
    is_calimed_back: claimed,
  };

  if (entryId) {
    entryRef = doc(entriesRef, entryId);
    const entrySnap = await getDoc(entryRef);

    if (entrySnap.exists()) {
      const existing = entrySnap.data();

      if (isClaimedBack(existing)) {
        claimed = true;
        baseFields.is_claimed_back = true;
        baseFields.is_calimed_back = true;
      }
    }

    if (!entrySnap.exists()) {
      await setDoc(entryRef, {
        ...baseFields,

        created_by_uid: audit.uid,
        created_by_email: audit.email,
        created_at: Timestamp.now(),

        updated_by_uid: audit.uid,
        updated_by_email: audit.email,
        updated_at: Timestamp.now(),
      });
    } else {
      await updateDoc(entryRef, {
        ...baseFields,

        updated_by_uid: audit.uid,
        updated_by_email: audit.email,
        updated_at: Timestamp.now(),
      });
    }
  } else {
    const created = await addDoc(entriesRef, {
      ...baseFields,

      created_by_uid: audit.uid,
      created_by_email: audit.email,
      created_at: Timestamp.now(),

      updated_by_uid: audit.uid,
      updated_by_email: audit.email,
      updated_at: Timestamp.now(),
    });

    entryId = created.id;
    entryRef = doc(entriesRef, entryId);
  }

  const weekNow = await getDoc(weekRef);
  const week = weekNow.exists() ? { id: weekNow.id, ...weekNow.data() } : null;

  const entriesQ = query(
    entriesRef,
    orderBy("expense_date", "asc"),
    orderBy("created_at", "asc")
  );

  const entriesSnap2 = await getDocs(entriesQ);
  const entries2 = entriesSnap2.docs.map((d) =>
    normaliseEntryRow(
      {
        id: d.id,
        ...d.data(),
      },
      week_ending_date
    )
  );

  const entrySnap2 = await getDoc(entryRef);
  const entry = entrySnap2.exists()
    ? normaliseEntryRow(
        {
          id: entrySnap2.id,
          ...entrySnap2.data(),
        },
        week_ending_date
      )
    : null;

  return { ok: true, venue_id, week_ending_date, week, entry, entries: entries2 };
}

/* =========================================================
   TOTAL
   petty_cash_week_id == week ending ISO doc id
   ✅ excludes claimed-back lines
========================================================= */
export async function pettyCashWeekTotal({ venue_id, petty_cash_week_id } = {}) {
  if (!venue_id) throw new Error("Missing venue_id");
  if (!petty_cash_week_id) throw new Error("Missing petty_cash_week_id");

  const weekId = String(petty_cash_week_id);
  if (!isISODate(weekId)) throw new Error("petty_cash_week_id must be YYYY-MM-DD");

  const weekRef = doc(db, "venues", venue_id, "petty_cash_weeks", weekId);
  const weekSnap = await getDoc(weekRef);

  if (!weekSnap.exists()) return 0;

  const entriesRef = collection(weekRef, "petty_cash_entries");
  const snap = await getDocs(entriesRef);

  const rows = snap.docs.map((d) => d.data());

  return num2(sumUnclaimed(rows));
}

/* =========================================================
   RESOLVE WEEK + ENTRIES + LATE + TOTAL
   ✅ totals exclude claimed-back lines
========================================================= */
export async function pettyCashWeekResolveTotal({ venue_id, week_ending_date } = {}) {
  const res = await pettyCashWeekList({ venue_id, week_ending_date });

  const week = res?.week ?? null;
  const entries = Array.isArray(res?.entries) ? res.entries : [];
  const late_entries = Array.isArray(res?.late_entries) ? res.late_entries : [];
  const weekId = week?.id ? String(week.id) : "";

  if (!weekId) {
    const total = sumUnclaimed(entries) + sumUnclaimed(late_entries);
    return { week, entries, late_entries, total: num2(total), weekId: "" };
  }

  let baseTotal = 0;

  try {
    baseTotal = await pettyCashWeekTotal({
      venue_id,
      petty_cash_week_id: weekId,
    });
  } catch {
    baseTotal = sumUnclaimed(entries);
  }

  const lateTotal = sumUnclaimed(late_entries);

  return {
    week,
    entries,
    late_entries,
    total: num2(Number(baseTotal || 0) + Number(lateTotal || 0)),
    weekId,
  };
}

/* =========================================================
   RESOLVE MERGED + SORTED ROWS FOR UI

   FIX:
   PettyCashView now receives all unclaimed rows:
   - current week unclaimed entries
   - previous week unclaimed entries
   - late entries not already included

   Claimed-back rows are not returned in rows.
========================================================= */
export async function pettyCashWeekResolveRows({ venue_id, week_ending_date } = {}) {
  const res = await pettyCashWeekList({ venue_id, week_ending_date });

  const entries = Array.isArray(res?.entries) ? res.entries : [];
  const late = Array.isArray(res?.late_entries) ? res.late_entries : [];
  const outstanding = Array.isArray(res?.outstanding_entries)
    ? res.outstanding_entries
    : [];

  const dedupe = new Map();

  function addRow(row, sourceType) {
    const normalised = normaliseEntryRow(
      row,
      row?._source_week_ending_date || row?.week_ending_date || week_ending_date
    );

    if (isClaimedBack(normalised)) return;

    const sourceWeekEnding =
      String(
        normalised?._source_week_ending_date ||
          normalised?.source_week_ending_date ||
          normalised?.week_ending_date ||
          week_ending_date
      ) || week_ending_date;

    const key = `${sourceWeekEnding}:${normalised.id}`;

    dedupe.set(key, {
      ...normalised,
      isLate: sourceType === "late",
      isOutstanding: sourceType === "outstanding",
      _rowKey: key,
      _source_week_ending_date: sourceWeekEnding,
      source_week_ending_date: sourceWeekEnding,
      week_ending_date: sourceWeekEnding,
    });
  }

  entries.forEach((e) => addRow(e, "current"));
  late.forEach((e) => addRow(e, "late"));
  outstanding.forEach((e) => addRow(e, "outstanding"));

  const rows = Array.from(dedupe.values());

  rows.sort((x, y) => {
    const ex = String(x?.expense_date || "");
    const ey = String(y?.expense_date || "");

    if (ex < ey) return -1;
    if (ex > ey) return 1;

    const wx = String(x?._source_week_ending_date || "");
    const wy = String(y?._source_week_ending_date || "");

    if (wx < wy) return -1;
    if (wx > wy) return 1;

    return toMillis(x?.created_at) - toMillis(y?.created_at);
  });

  const total = sumUnclaimed(rows);

  return {
    week: res?.week ?? null,
    weekId: res?.week?.id ? String(res.week.id) : "",
    entries,
    late_entries: late,
    outstanding_entries: outstanding,
    rows,
    total: num2(total),
  };
}

/* =========================================================
   REVENUE HELPERS
   Reads weekly revenue from:
   venues/{venue_id}/tills_days/{trading_date}/tills/*
========================================================= */
export async function pettyCashWeekRevenueTotal({ venue_id, week_ending_date } = {}) {
  if (!venue_id) throw new Error("Missing venue_id");
  if (!week_ending_date) throw new Error("Missing week_ending_date");
  if (!isISODate(week_ending_date)) {
    throw new Error("week_ending_date must be YYYY-MM-DD");
  }

  const dates = getWeekRangeDates(week_ending_date);

  let total = 0;

  for (const trading_date of dates) {
    const tillsRef = collection(
      db,
      "venues",
      venue_id,
      "tills_days",
      trading_date,
      "tills"
    );

    const tillsSnap = await getDocs(tillsRef);

    tillsSnap.forEach((d) => {
      const row = d.data() || {};

      const declared = Number(row?.declared_total);
      const counted = Number(row?.counted_total);

      if (Number.isFinite(declared) && declared > 0) {
        total += declared;
      } else if (Number.isFinite(counted) && counted > 0) {
        total += counted;
      }
    });
  }

  return num2(total);
}

/* =========================================================
   RESOLVE PETTY CASH VS REVENUE
========================================================= */
export async function pettyCashVsRevenueResolve({ venue_id, week_ending_date } = {}) {
  if (!venue_id) throw new Error("Missing venue_id");
  if (!week_ending_date) throw new Error("Missing week_ending_date");
  if (!isISODate(week_ending_date)) {
    throw new Error("week_ending_date must be YYYY-MM-DD");
  }

  const week_start_date = addDaysUTC(week_ending_date, -6);

  const [pettyCashRes, revenueTotal] = await Promise.all([
    pettyCashWeekResolveTotal({ venue_id, week_ending_date }),
    pettyCashWeekRevenueTotal({ venue_id, week_ending_date }),
  ]);

  const pettyCashTotal = num2(pettyCashRes?.total || 0);
  const actualRevenueTotal = num2(revenueTotal || 0);

  const pct_of_revenue =
    actualRevenueTotal > 0
      ? num2((pettyCashTotal / actualRevenueTotal) * 100)
      : 0;

  return {
    ok: true,
    venue_id,
    week_ending_date,
    week_start_date,
    week_range: {
      from: week_start_date,
      to: week_ending_date,
    },
    petty_cash_total: pettyCashTotal,
    actual_revenue_total: actualRevenueTotal,
    pct_of_revenue,
    petty_cash: pettyCashRes,
  };
}